// src/components/ActiveSearchFilterChips/ActiveSearchFilterChips.jsx

'use client';


import { useSearchParams, usePathname, useRouter } from 'next/navigation';
import { Chip, Box } from '@mui/material';
import { useEffect, useState } from 'react';
import styles from '@/app/[lang]/shop/shop-category.module.scss';


const labels = {
  en: {
    size: 'Sizes',
    mainColors: 'Main Colors',
    styles: 'Styles',
    prices: 'Price',
    manufacturing: 'Manufacturing',
    weavedMaterials: 'Materials',
    origins: 'Origin',
    productID: 'Product ID',
    delivery: 'Delivery',
    usage: 'Usage',
    pileThickness: 'Pile Thickness',
    clearFilters: 'Clear filters',
    activeFilters: 'Active Search Filters:',
  },
  fr: {
    size: 'Tailles',
    mainColors: 'Couleurs principales',
    styles: 'Styles',
    prices: 'Prix',
    manufacturing: 'Fabrication',
    weavedMaterials: 'Matériaux',
    origins: 'Origine',
    productID: 'ID du produit',
    delivery: 'Livraison',
    usage: 'Utilisation',
    pileThickness: 'Épaisseur du poil',
    clearFilters: 'Effacer les filtres',
    activeFilters: 'Filtres de recherche actifs:',
  },
  it: {
    size: 'Dimensioni',
    mainColors: 'Colori principali',
    styles: 'Stili',
    prices: 'Prezzo',
    manufacturing: 'Produzione',
    weavedMaterials: 'Materiali',
    origins: 'Origine',
    productID: 'ID prodotto',
    delivery: 'Consegna',
    usage: 'Uso',
    pileThickness: 'Spessore del pelo',
    clearFilters: 'Cancella filtri',
    activeFilters: 'Filtri di ricerca attivi:',
  },
  es: {
    size: 'Tamaños',
    mainColors: 'Colores principales',
    styles: 'Estilos',
    prices: 'Precio',
    manufacturing: 'Fabricación',
    weavedMaterials: 'Materiales',
    origins: 'Origen',
    productID: 'ID del producto',
    delivery: 'Entrega',
    usage: 'Uso',
    pileThickness: 'Grosor del pelo',
    clearFilters: 'Borrar filtros',
    activeFilters: 'Filtros de búsqueda activos:',
  },
};


const filterKeys = [
  'size',
  'mainColors',
  'styles',
  'prices',
  'manufacturing',
  'weavedMaterials',
  'origins',
  'productID',
  'delivery',
  'usage',
  'pileThickness',
];


export default function ActiveSearchFilterChips({ lang }) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const [activeFilters, setActiveFilters] = useState([]);

  useEffect(() => {
    const active = filterKeys.reduce((acc, key) => {
      const value = searchParams.get(key);
      if (value) {
        acc.push({ key, value });
      }
      return acc;
    }, []);
    setActiveFilters(active);
  }, [searchParams]);

  const handleDelete = (key) => {
    const params = new URLSearchParams(searchParams.toString());
    params.delete(key);
    const newQuery = params.toString();
    router.push(`${pathname}${newQuery ? `?${newQuery}` : ''}`);
  };

  const handleClearAll = () => {
    const params = new URLSearchParams(searchParams.toString());
    filterKeys.forEach((key) => {
      params.delete(key);
    });
    const newQuery = params.toString();
    router.push(`${pathname}${newQuery ? `?${newQuery}` : ''}`);
  };

  if (activeFilters.length === 0) {
    return null;
  }

  // Custom styles for the wrapping chips
  const chipStyles = {
    margin: '2px',
    height: 'auto',
    '& .MuiChip-label': {
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      display: 'block',
      lineHeight: '1.2em',
      padding: '8px 12px',
    },
  };

  return (
    <Box sx={{ padding: 2 }} className={styles.chipsContainer}>
      <h2 style={{ fontSize: '1em' }}>{labels[lang].activeFilters}</h2>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {activeFilters.map(({ key, value }) => (
          <Chip
            key={key}
            label={`${labels[lang][key]}: ${value}`}
            onDelete={() => handleDelete(key)}
            sx={chipStyles}
          />
        ))}
        {activeFilters.length > 2 && (
          <Chip
            label={labels[lang].clearFilters}
            onClick={handleClearAll}
            sx={{
              ...chipStyles,
              backgroundColor: 'rgba(224,224,224,.63)',
              fontWeight: 'bold',
            }}
          />
        )}
      </Box>
    </Box>
  );
}
